import { useIntl } from 'react-intl';

import { tokens } from '@fhs/ui';
import { HStack, Text, VStack } from '@fhs-legacy/universal-components';
import Currency from 'components/currency';
import { formatExpirationDate } from 'components/payments/components/payment-method-option/payment-method-option-types/utils';
import { usePaymentContext } from 'state/payment';
import { IPaymentMethod } from 'state/payment/types';

import { MethodType } from './styled';

type GiftCardMethod = {
  method: IPaymentMethod;
  isDisabled?: boolean;
  hasError?: boolean;
  showBalance?: boolean;
};

export function GiftCardMethod({
  method,
  isDisabled = false,
  hasError = false,
  showBalance = false,
}: GiftCardMethod) {
  const { giftCard } = method;
  const { formatMessage } = useIntl();
  const { getBalanceFromGiftCard, hasGetPaymentMethodsError } = usePaymentContext();

  const validTextColor = isDisabled ? tokens.colors.$disabledText : tokens.colors.$houseDark;
  const textColor = hasError ? tokens.colors.$error : validTextColor;

  if (!giftCard) {
    return null;
  }

  const giftCardAlias = (
    <Text color={textColor}>
      {formatMessage({ id: 'endsIn' }).toLowerCase()} {giftCard.last4}
    </Text>
  );

  return (
    <HStack alignItems={'center'} justifyItems={'left'}>
      <VStack>
        <HStack space={2}>
          <MethodType color={textColor} textTransform="capitalize">
            Gift Card
          </MethodType>
          <MethodType color={textColor} textTransform="capitalize">
            {giftCardAlias}
          </MethodType>
          <Text color={textColor} testID="payment-method-expirationDate" textTransform="capitalize">
            {formatExpirationDate({
              expiryMonth: giftCard.expiryMonth ?? '',
              expiryYear: giftCard.expiryYear ?? '',
              formatMessage,
            })}
          </Text>
        </HStack>
        {showBalance && (
          <HStack space={1}>
            <Text color={textColor} fontSize="xs">
              Balance:
            </Text>
            {hasGetPaymentMethodsError ? (
              <>
                <Text color={tokens.colors.$error} fontSize="xs">
                  ---
                </Text>
                <Text color={tokens.colors.$error} fontSize="xs">
                  {formatMessage({ id: 'balanceLoadingError' })}
                </Text>
              </>
            ) : (
              <Currency
                amount={getBalanceFromGiftCard(giftCard)}
                textProps={{
                  margin: 0,
                  fontSize: 12,
                  color: textColor,
                }}
              />
            )}
          </HStack>
        )}
      </VStack>
    </HStack>
  );
}
